/**
 * Guest session ID management for cart persistence
 *
 * The session ID is generated and signed by the backend.
 * The frontend stores the signed session ID received from the backend's
 * X-Session-ID response header and uses it for subsequent requests.
 */

const SESSION_ID_KEY = 'guest_session_id';

/**
 * Get the session ID from localStorage
 * @returns The session ID string if it exists, empty string otherwise
 *
 * Note: Session IDs are generated by the backend. If no session exists,
 * the backend will create one and return it in the X-Session-ID response header.
 * The apiClient interceptor automatically stores it in localStorage.
 */
export function getSessionId(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  return localStorage.getItem(SESSION_ID_KEY) || '';
}

/**
 * Check if a session ID exists without creating one
 * @returns The session ID if it exists, null otherwise
 */
export function getExistingSessionId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(SESSION_ID_KEY);
}

/**
 * Clear the session ID from localStorage
 * Call this after successful login when cart has been merged
 */
export function clearSessionId(): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.removeItem(SESSION_ID_KEY);
}

/**
 * Hook for managing guest session IDs
 * @returns Object with sessionId, getSessionId, and clearSessionId functions
 */
export function useSessionId() {
  return {
    /**
     * Get or create session ID
     */
    getSessionId,

    /**
     * Get existing session ID without creating
     */
    getExistingSessionId,

    /**
     * Clear session ID (call after login)
     */
    clearSessionId,
  };
}
